import { toast } from "react-toastify";
import { getCrewUserByUserId, getOrgDetails } from "../API/index";
import { setOrganisationDetails } from "../redux/slices/organisationDetailsSlice";

export const getSafeInternalRedirect = (redirectPath) => {
  if (!redirectPath || typeof redirectPath !== "string") {
    return "";
  }

  const trimmedPath = redirectPath.trim();

  if (!trimmedPath || trimmedPath.startsWith("//")) {
    return "";
  }

  try {
    const hasProtocol = /^[a-z][a-z\d+\-.]*:/i.test(trimmedPath);
    const normalizedPath =
      trimmedPath.startsWith("/") || hasProtocol ? trimmedPath : `/${trimmedPath}`;
    const redirectUrl = new URL(normalizedPath, window.location.origin);

    if (redirectUrl.origin !== window.location.origin) {
      return "";
    }

    if (redirectUrl.pathname === "/sso") {
      return "";
    }

    return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
  } catch (error) {
    return "";
  }
};

const getStoredUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("user_id"));
  } catch (error) {
    return "";
  }
};

export const getDefaultLoginRedirect = (roleName = localStorage.getItem("role_name")) => {
  switch (roleName) {
    case "Admin":
    case "Foreman":
    case "Foreman OpsManager":
      return "/time-sheet";
    case "Foreman GeneralForeman": {
      const userId = getStoredUserId();
      return userId ? `/time-sheet/${userId}` : "/time-sheet";
    }
    case "General Foreman":
    case "Ops Manager":
    case "GeneralForeman Ops Manager":
      return "/review-timesheet";
    case "User":
      return "/no-data";
    case "Checklist Approver":
      return "/project-checklist";
    default:
      return "/";
  }
};

const storeOrganizationDetails = async ({ dispatch, updateTheme }) => {
  const orgRes = await getOrgDetails();

  if (orgRes?.data) {
    const formattedObject = orgRes.data.reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {});

    localStorage.setItem("themeData", JSON.stringify(formattedObject));
    updateTheme?.(formattedObject);
    dispatch?.(setOrganisationDetails(orgRes.data));
  }
};

const setUserRoleFromCrewRoles = (roleId, crewUsers) => {
  if (roleId === 1) {
    localStorage.setItem("role_name", "Admin");
    return "/time-sheet";
  }

  if (roleId !== 2) {
    return "/";
  }

  const crewRoles = {};
  crewUsers
    ?.filter((item) => item.crew?.id)
    .forEach((item) => {
      crewRoles[item?.crew_role_id] = item;
    });

  const isUserForeman = crewRoles[1];
  const isUserGeneralForeman = crewRoles[2];
  const isUserOpsManager = crewRoles[3];
  const isStepUpForeman = crewRoles[5];

  if ((isUserForeman || isStepUpForeman) && !isUserGeneralForeman && !isUserOpsManager) {
    localStorage.setItem("role_name", "Foreman");
    localStorage.setItem(
      "user_id",
      JSON.stringify(isUserForeman ? isUserForeman?.user_id : isStepUpForeman?.user_id)
    );
    return "/time-sheet";
  }

  if (isUserGeneralForeman && !isUserForeman && !isUserOpsManager) {
    localStorage.setItem("role_name", "General Foreman");
    localStorage.setItem("user_id", JSON.stringify(isUserGeneralForeman?.user_id));
    return "/review-timesheet";
  }

  if (isUserOpsManager && !isUserGeneralForeman && !isUserForeman) {
    localStorage.setItem("role_name", "Ops Manager");
    localStorage.setItem("user_id", JSON.stringify(isUserOpsManager?.user_id));
    return "/review-timesheet";
  }

  if (isUserForeman && isUserGeneralForeman) {
    localStorage.setItem("role_name", "Foreman GeneralForeman");
    localStorage.setItem("user_id", JSON.stringify(isUserForeman?.user_id));
    return `/time-sheet/${isUserForeman?.user_id}`;
  }

  if (isUserForeman && isUserOpsManager && !isUserGeneralForeman) {
    localStorage.setItem("role_name", "Foreman OpsManager");
    localStorage.setItem("user_id", JSON.stringify(isUserForeman?.user_id));
    return "/time-sheet";
  }

  if (!isUserForeman && isUserOpsManager && isUserGeneralForeman) {
    localStorage.setItem("role_name", "GeneralForeman Ops Manager");
    localStorage.setItem("user_id", JSON.stringify(isUserGeneralForeman?.user_id));
    return "/review-timesheet";
  }

  const isChecklistApprover = JSON.parse(localStorage.getItem("checklist_approver") || "false");
  if (isChecklistApprover) {
    localStorage.setItem("role_name", "Checklist Approver");
    return "/project-checklist";
  }

  localStorage.setItem("role_name", "User");
  return "/no-data";
};

export const isChecklistApproverFlag = () =>
  JSON.parse(localStorage.getItem("checklist_approver") || "false");

// Roles whose crew role natively includes timesheet review powers.
const NATIVE_REVIEW_ROLES = [
  "Admin",
  "Ops Manager",
  "General Foreman",
  "Foreman GeneralForeman",
  "Foreman OpsManager",
  "GeneralForeman Ops Manager",
];

// crew_role_ids whose crews each review role may act on (mirrors the
// scoping ReviewTimeSheet uses when fetching a native reviewer's crews).
const REVIEW_ROLE_CREW_ROLE_IDS = {
  "General Foreman": [2],
  "Foreman GeneralForeman": [2],
  "Ops Manager": [3],
  "Foreman OpsManager": [3],
  "GeneralForeman Ops Manager": [2, 3],
};

/**
 * Crew ids the current user may MUTATE on the review-timesheet pages.
 * The checklist_approver flag widens what a user can SEE to all crews, but
 * write actions must stay confined to the crews their native role reviews.
 *
 * Returns null when the user is not scope-limited (no approver widening, or
 * Admin), otherwise a Set of crew ids (empty = view-only everywhere).
 */
export const getReviewableCrewIds = async (roleName, userId) => {
  if (!isChecklistApproverFlag()) return null;
  if (roleName === "Admin") return null;
  if (!NATIVE_REVIEW_ROLES.includes(roleName) || !userId) return new Set();

  const crewRes = await getCrewUserByUserId(userId);
  if (crewRes?.status !== 200) {
    // Fail closed, but not silently — a native reviewer shouldn't wonder
    // where their Approve buttons went
    toast.error(
      "Could not verify your review permissions — timesheets are view-only for now"
    );
    return new Set();
  }

  const allowedRoleIds = REVIEW_ROLE_CREW_ROLE_IDS[roleName] || [];
  const crewIds = (crewRes?.data || [])
    .filter((item) => allowedRoleIds.includes(item?.crew_role_id))
    .map((item) => Number(item?.crew_id));
  return new Set(crewIds);
};

export const completeLogin = async (loginResponse, { dispatch, updateTheme } = {}) => {
  if (loginResponse?.status !== 200) {
    return "";
  }

  const user = loginResponse?.data?.user;
  const roleId = Number(user?.role_id);
  const accessToken = loginResponse?.data?.access_token;

  if (!accessToken || !user?.id || Number.isNaN(roleId)) {
    return "";
  }

  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("role_id", roleId);
  localStorage.setItem("name", user?.name);
  localStorage.setItem("organization_id", user?.organization_id);
  localStorage.setItem("checklist_approver", user?.checklist_approver || false);

  await storeOrganizationDetails({ dispatch, updateTheme });

  if (roleId === 1) {
    return setUserRoleFromCrewRoles(roleId);
  }

  const crewUserRes = await getCrewUserByUserId(user?.id);
  return setUserRoleFromCrewRoles(roleId, crewUserRes?.status === 200 ? crewUserRes?.data : []);
};
